import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy, ImageDown, Link2, Loader2, Mail, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { assistantName } from '../../hooks/useCompanyBrand';

// Creates a short link that reopens the shopper's exact configuration.
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.7.8-.8 1-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3a.5.5 0 0 0 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6a2.7 2.7 0 0 0 1.8-1.2 2.2 2.2 0 0 0 .1-1.3c0-.1-.2-.2-.5-.3z"/></svg>
);
const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.4 1.8-2.4.9 0 1.3.6 1.3 1.4 0 .9-.5 2.1-.8 3.3-.2 1 .5 1.8 1.5 1.8 1.8 0 3.2-1.9 3.2-4.7 0-2.4-1.8-4.2-4.3-4.2a4.5 4.5 0 0 0-4.7 4.5c0 .9.3 1.8.8 2.3a.3.3 0 0 1 0 .3l-.3 1.2c0 .2-.2.3-.4.2-1.3-.6-2.1-2.5-2.1-4 0-3.3 2.4-6.3 6.9-6.3 3.6 0 6.4 2.6 6.4 6 0 3.6-2.3 6.5-5.4 6.5-1.1 0-2.1-.6-2.4-1.2l-.7 2.5c-.2.9-.9 2.1-1.3 2.8A10 10 0 1 0 12 2z"/></svg>
);

export const canNativeShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function';
export const nativeShare = (url, title) => navigator.share({ title: title || `My ${assistantName()} shade design`, text: `Take a look at the shade I designed with ${assistantName()}:`, url });

export const ShareDesignDialog = ({ open, onOpenChange, createLink, onEmail, shareTitle }) => {
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) { setLink(''); setCopied(false); return; }
    let cancelled = false;
    setLoading(true);
    createLink()
      .then((url) => { if (!cancelled) setLink(url); })
      .catch((err) => toast.error(err?.response?.data?.detail || 'Could not create a share link'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Copy failed — select the link and copy it manually');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-design-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Link2 className="w-5 h-5 text-amber-600" /> Share this design</DialogTitle>
          <DialogDescription>Anyone with this link sees your exact fabric, size and options — and can pick up right where you left off.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input readOnly value={loading ? 'Creating link…' : link} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid="share-design-link" />
          <Button onClick={copy} disabled={!link || loading} className="bg-neutral-900 hover:bg-neutral-800 gap-1.5 flex-shrink-0" data-testid="share-design-copy">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        {canNativeShare() && (
          <Button onClick={() => nativeShare(link, shareTitle).catch(() => {})} disabled={!link || loading} variant="outline" className="w-full gap-2 h-11" data-testid="share-design-native">
            <Share2 className="w-4 h-4" /> Share via Messages, WhatsApp, email…
          </Button>
        )}
        {!canNativeShare() && link && !loading && (
          <div className="grid grid-cols-2 gap-2" data-testid="share-design-social">
            <a href={`https://wa.me/?text=${encodeURIComponent(`Take a look at the shade I designed with ${assistantName()}: ${link}`)}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-10 rounded-md bg-[#25D366] text-white text-sm font-medium transition-opacity hover:opacity-90" data-testid="share-design-whatsapp">
              <WhatsAppIcon /> WhatsApp
            </a>
            <a href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(link)}&media=${encodeURIComponent(link.includes('/api/share/') ? `${link}/pin.jpg` : '')}&description=${encodeURIComponent(shareTitle || `My ${assistantName()} shade design`)}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-10 rounded-md bg-[#E60023] text-white text-sm font-medium transition-opacity hover:opacity-90" data-testid="share-design-pinterest">
              <PinterestIcon /> Pinterest
            </a>
          </div>
        )}
        {link && !loading && link.includes('/api/share/') && (
          <a href={`${link}/pin.jpg`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800" data-testid="share-design-pin-image">
            <ImageDown className="w-3.5 h-3.5" /> Get the tall Pin image (1000 × 1500)
          </a>
        )}
        {onEmail && (
          <button type="button" onClick={onEmail} className="text-sm text-amber-700 hover:text-amber-900 underline flex items-center gap-1 self-start" data-testid="share-design-email">
            <Mail className="w-3.5 h-3.5" /> Email this design to myself instead
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDesignDialog;
